import { useState, useEffect, useRef } from 'react'
import { Search, X, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { motion, AnimatePresence } from 'framer-motion'

const ClientSearch = ({ onSelectClient, selectedClientId = null }) => {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [clients, setClients] = useState([])
  const [filteredClients, setFilteredClients] = useState([])
  const [loading, setLoading] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedClient, setSelectedClient] = useState(null)
  const organizationId = useOrganizationId()
  const searchRef = useRef(null)
  const dropdownRef = useRef(null)

  useEffect(() => {
    if (organizationId && searchTerm.length >= 2) {
      fetchClients()
    } else {
      setFilteredClients([])
    }
  }, [searchTerm, organizationId])

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        searchRef.current &&
        !searchRef.current.contains(event.target)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (selectedClientId && clients.length > 0) {
      const client = clients.find((c) => c.id === selectedClientId)
      if (client) {
        setSelectedClient(client)
        setSearchTerm(`${client.first_name} ${client.last_name}`)
      }
    }
  }, [selectedClientId, clients])

  const fetchClients = async () => {
    if (!organizationId) return
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('clients')
        .select('id, first_name, last_name, date_of_birth, gender, phone, email')
        .eq('organization_id', organizationId)
        .or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`)
        .order('first_name')
        .limit(10)

      if (error) throw error
      setClients(data || [])
      setFilteredClients(data || [])
      setShowDropdown(data && data.length > 0)
    } catch (error) {
      console.error('Error fetching clients:', error)
      setFilteredClients([])
    } finally {
      setLoading(false)
    }
  }

  const handleSelectClient = (client) => {
    setSelectedClient(client)
    setSearchTerm(`${client.first_name} ${client.last_name}`)
    setShowDropdown(false)
    onSelectClient(client)
  }

  const handleClear = () => {
    setSelectedClient(null)
    setSearchTerm('')
    setFilteredClients([])
    onSelectClient(null)
  }

  return (
    <div className="relative">
      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
        {t('examination.searchExistingClient')}
      </label>
      <div className="relative" ref={searchRef}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setSelectedClient(null)
              if (e.target.value.length >= 2) {
                setShowDropdown(true)
              }
            }}
            onFocus={() => {
              if (filteredClients.length > 0) {
                setShowDropdown(true)
              }
            }}
            placeholder={t('examination.searchByPlaceholder')}
            className="w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {selectedClient && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={18} />
            </button>
          )}
        </div>

        <AnimatePresence>
          {showDropdown && filteredClients.length > 0 && (
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto"
            >
              {loading ? (
                <div className="p-4 text-center text-sm text-gray-500">Loading...</div>
              ) : (
                filteredClients.map((client) => (
                  <button
                    key={client.id}
                    type="button"
                    onClick={() => handleSelectClient(client)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary-400 to-secondary-400 flex items-center justify-center text-white font-semibold">
                      {client.first_name?.[0]}{client.last_name?.[0]}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {client.first_name} {client.last_name}
                      </div>
                      {client.email && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">{client.email}</div>
                      )}
                      {client.phone && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">{client.phone}</div>
                      )}
                    </div>
                  </button>
                ))
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {selectedClient && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-2 p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg flex items-center gap-2"
        >
          <User size={16} className="text-primary-600 dark:text-primary-400" />
          <span className="text-sm text-primary-700 dark:text-primary-300">
            {t('examination.clientSelected', { name: `${selectedClient.first_name} ${selectedClient.last_name}` })}
          </span>
        </motion.div>
      )}
    </div>
  )
}

export default ClientSearch

